/**
 * PartnerReachModal — outreach picker + WhatsApp deeplink composer.
 *
 * Usage:
 *   <PartnerReachModal
 *     partner={{ id, name, phone, slug, leads_count, customers_count, ... }}
 *     onClose={() => setOpen(null)}
 *     onSent={() => refetch()}    // optional — fires after wa.me click + log
 *   />
 *
 * Flow inside the modal:
 *   1. Load the static template catalogue once (cached per session).
 *   2. Render a 3-column list of templates grouped by category.
 *   3. On select → POST /admin/partner-reach/render to resolve tokens.
 *   4. Show a live-editable preview pane on the right.
 *   5. Two CTAs:
 *        - "WhatsApp öffnen" → opens wa.me link in new tab + logs the intent.
 *        - "Text kopieren" → fallback when the partner has no phone.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, MessageCircle, Copy, Loader2, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function authHeader() {
  const t = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : "";
  return { "X-Admin-Token": t || "" };
}

const CATEGORY_COLOR = {
  "Onboarding-Push": "bg-violet-100 text-violet-700",
  "Gratulation": "bg-emerald-100 text-emerald-700",
  "Reaktivierung": "bg-amber-100 text-amber-700",
  "Tipps & Tricks": "bg-sky-100 text-sky-700",
  "Webinar-Push": "bg-rose-100 text-rose-700",
  "Custom": "bg-slate-100 text-slate-700",
};

export default function PartnerReachModal({ partner, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [rendered, setRendered] = useState("");
  const [waUrl, setWaUrl] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  // 1. Load templates + auto-select the suggestion if the parent passed _preselect
  useEffect(() => {
    axios.get(`${API}/admin/partner-reach/templates`, { headers: authHeader() })
      .then((r) => {
        setTemplates(r.data.templates || []);
        const pre = partner?._preselect;
        if (pre) {
          const t = (r.data.templates || []).find((x) => x.id === pre);
          if (t) {
            setSelectedId(t.id);
            renderTemplate(t.id);
          }
        }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Vorlagen-Liste fehlgeschlagen."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Render the selected template (or custom body if the admin edited it)
  const renderTemplate = async (templateId, customBody) => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/partner-reach/render`,
        {
          partner_id: partner.id,
          template_id: templateId,
          ...(customBody !== undefined ? { custom_body: customBody } : {}),
        },
        { headers: authHeader() },
      );
      setRendered(r.data.rendered);
      setWaUrl(r.data.wa_url);
      setStats(r.data.partner);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rendern fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  // When the admin types in the preview area, debounce re-render with tokens.
  // We re-call the backend so {vorname}/{anzahl_leads}/etc keep resolving.
  useEffect(() => {
    if (!editing || !selectedId) return;
    const t = setTimeout(() => renderTemplate(selectedId, rendered), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rendered, editing]);

  const pickTemplate = (tpl) => {
    setSelectedId(tpl.id);
    setEditing(false);
    renderTemplate(tpl.id);
  };

  const openWa = async () => {
    if (!waUrl) {
      toast.error("Keine Telefon-Nummer für diesen Partner hinterlegt.");
      return;
    }
    window.open(waUrl, "_blank", "noopener,noreferrer");
    // log the intent
    try {
      await axios.post(
        `${API}/admin/partner-reach/log`,
        { partner_id: partner.id, template_id: selectedId || "custom", body: rendered },
        { headers: authHeader() },
      );
      toast.success("WhatsApp geöffnet — Nachricht im Log gespeichert.");
      onSent && onSent();
    } catch {
      toast.message("WhatsApp geöffnet (Log fehlgeschlagen, egal — Nachricht ist auf).");
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(rendered);
      toast.success("In die Zwischenablage kopiert.");
    } catch {
      toast.error("Kopieren nicht möglich — bitte manuell markieren.");
    }
  };

  const grouped = templates.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="partner-reach-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 sm:px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-900 truncate">
              WhatsApp-Nachricht an {partner.name || "Partner"}
            </div>
            <div className="text-xs text-slate-500 font-mono-tab">
              {partner.phone || "📵 keine Nummer hinterlegt"}
              {stats ? (
                <span className="ml-3">
                  · {stats.leads_count} Leads · {stats.customers_count} Kunden
                  {stats.days_since_last_lead !== null
                    ? ` · letzter Lead vor ${stats.days_since_last_lead} d`
                    : " · noch kein Lead"}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="partner-reach-close"
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
          {/* Left: templates */}
          <div className="p-5 sm:p-6">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-3">
              Vorlage auswählen
            </div>
            <div className="space-y-5">
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <div className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full mb-2 ${CATEGORY_COLOR[cat] || "bg-slate-100 text-slate-700"}`}>
                    {cat}
                  </div>
                  <div className="space-y-1.5">
                    {items.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => pickTemplate(t)}
                        data-testid={`partner-reach-tpl-${t.id}`}
                        className={`w-full text-left p-2.5 rounded-lg border transition-colors text-sm ${
                          selectedId === t.id
                            ? "bg-violet-50 border-violet-300 ring-2 ring-violet-200"
                            : "bg-white border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <div className="font-semibold text-slate-900">{t.label}</div>
                        {t.use_case ? (
                          <div className="text-xs text-slate-500 mt-0.5">{t.use_case}</div>
                        ) : null}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: preview */}
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                Vorschau {editing ? "(bearbeitbar)" : ""}
              </div>
              <button
                type="button"
                onClick={() => setEditing(!editing)}
                disabled={!selectedId}
                data-testid="partner-reach-toggle-edit"
                className="text-xs text-violet-700 hover:underline disabled:opacity-40"
              >
                {editing ? "Bearbeiten beenden" : "Manuell anpassen"}
              </button>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm py-12 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> rendere …
              </div>
            ) : selectedId ? (
              editing ? (
                <textarea
                  value={rendered}
                  onChange={(e) => setRendered(e.target.value)}
                  data-testid="partner-reach-editor"
                  className="w-full h-72 p-3 rounded-lg border border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-200 font-mono-tab text-sm text-slate-800 whitespace-pre-wrap"
                />
              ) : (
                <pre
                  data-testid="partner-reach-preview"
                  className="w-full p-3 rounded-lg bg-slate-50 border border-slate-200 font-sans text-sm text-slate-800 whitespace-pre-wrap min-h-[18rem]"
                >{rendered}</pre>
              )
            ) : (
              <div className="text-slate-400 text-sm py-12 text-center italic flex flex-col items-center gap-2">
                <Sparkles className="h-6 w-6 text-violet-200" />
                Wähle links eine Vorlage — Tokens werden automatisch eingesetzt.
              </div>
            )}

            {selectedId ? (
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={openWa}
                  disabled={!waUrl}
                  data-testid="partner-reach-open-wa"
                  className="flex-1 min-w-[180px] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp öffnen
                </button>
                <button
                  type="button"
                  onClick={copyText}
                  data-testid="partner-reach-copy"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-800 font-semibold text-sm"
                >
                  <Copy className="h-4 w-4" /> Text kopieren
                </button>
              </div>
            ) : null}

            {selectedId ? (
              <div className="mt-3 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-900 leading-relaxed">
                ℹ️ Die Nachricht enthält am Ende eine kurze Info, dass es sich um eine automatisierte Coaching-Vorlage von Alphalane handelt
                — so weiß der Partner sofort, dass es kein persönlich getippter Text ist. Wenn er „stopp" zurückschreibt, kommen keine weiteren.
              </div>
            ) : null}

            {selectedId && !waUrl ? (
              <div className="mt-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                Dieser Partner hat keine Telefon-Nummer hinterlegt. Du kannst den Text trotzdem kopieren
                und manuell verschicken.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
